import { Injectable } from '@angular/core';
import { ResourceObj } from '../classes/resource-obj';
import * as firebase from 'firebase';
import { RoleObj } from '../classes/role-obj';
import { UserObj } from '../classes/user-obj';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { PermissionObj } from '../classes/permission-obj';
import { Operation } from '../classes/permission-obj';
import { take, map, catchError } from 'rxjs/operators';

@Injectable()
export class AuthGuard implements CanActivate {
  constructor(private router: Router, private authorizatorservice: AuthorizatorService) {}

  private isAuthorized(rootFirebasePath: string, resource: string): Observable<string> {
    return new Observable(observer => {
      firebase.auth().onAuthStateChanged(user => {
        if (user) {
          if (resource) {
            Promise.all([
              this.authorizatorservice.getUser(rootFirebasePath, user.email),
              this.authorizatorservice.getRolesByUser(rootFirebasePath, user.email)
            ]).then(values => {
              const userDB: UserObj = values[0];
              const rolesDB: RoleObj[] = values[1] || [];
              const resourcePermissionsByUser: PermissionObj[] = [];
              const resourcePermissionsByRoles: PermissionObj[] = [];
              if (userDB && userDB.data.permissions) {
                userDB.data.permissions.filter(
                  permission => permission.resource === resource || permission.resource === 'owner'
                ).forEach(permission => {
                  resourcePermissionsByUser.push(permission);
                });
              }
              rolesDB.forEach(rolDB => {
                if (rolDB.data.permissions) {
                  rolDB.data.permissions.filter(permission => permission.resource === resource).forEach(permission => {
                    resourcePermissionsByRoles.push(permission);
                  });
                }
              });
              if (resourcePermissionsByUser && resourcePermissionsByUser.length > 0 &&
                resourcePermissionsByUser.findIndex(
                  permission => permission.operations && permission.operations.findIndex(operation => operation === Operation.Deny) > -1
                ) === -1
              ) {
                  observer.next('authorized');
              } else {
                if (resourcePermissionsByRoles && resourcePermissionsByRoles.length > 0 &&
                  resourcePermissionsByRoles.findIndex(
                    permission => permission.operations.findIndex(operation => operation === Operation.Deny) > -1
                  ) === -1
                ) {
                    observer.next('authorized');
                } else {
                  console.error('Unauthorized resource');
                  observer.next('unauthorized');
                }
              }
            }).catch(error => {
              console.error('Error getting permissions from database');
              console.error(error);
              observer.next('unauthorized');
            });
          } else {
            console.error('Resource not received');
            observer.next('unauthorized');
          }
        } else {
          console.error('Unauthenticated user');
          observer.next('unauthenticated');
        }
      },
      err => {
        observer.error(err);
      },
      () => {
        observer.complete();
      });
    });
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    let rootFirebasePath: string = route.data.rootFirebasePath;
    const resource = route.data.resource;
    const anonymousRoute = route.data.anonymousRoute || '/';
    const unauthorizedRoute = route.data.unauthorizedRoute || '/';
    const findVariable = rootFirebasePath.indexOf(':');
    if (findVariable > -1) {
      const variableEnd = rootFirebasePath.indexOf('/', findVariable);
      let variableFound;
      if (variableEnd > -1) {
        variableFound = rootFirebasePath.substring(findVariable + 1, variableEnd);
      } else {
        variableFound = rootFirebasePath.substring(findVariable + 1);
      }
      const existsVariable = Object.keys(route.params).findIndex(key => key === variableFound);
      if (existsVariable > -1) {
        rootFirebasePath = rootFirebasePath.replace(':' + variableFound, route.params[variableFound]);
      }
    }

    return this.isAuthorized(rootFirebasePath, resource).pipe(
      take(1),
      map(isAuth => {
        if (isAuth === 'unauthenticated') {
          this.router.navigate([anonymousRoute], { queryParams: { returnUrl: state.url }});
          return false;
        }
        if (isAuth === 'unauthorized') {
          this.router.navigate([unauthorizedRoute], { queryParams: { returnUrl: state.url }});
          return false;
        }
        if (isAuth === 'authorized') {
          return true;
        }
        return false;
      }),
      catchError((err) => {
        console.error('Error getting authenticated user');
        console.error(err);
        // not resource sended so redirect to unauthorized page with the return url
        this.router.navigate([anonymousRoute], { queryParams: { returnUrl: state.url }});
        return of(false);
      })
    );
  }
}

@Injectable()
export class AuthorizatorService {

  constructor() { }

  //#region Users
  addUser(rootFirebasePath: string, user: UserObj): Promise<null> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      rootFirebasePath = rootFirebasePath || '';
      const path = (rootFirebasePath + '/users').replace('//', '/');
      firestore.collection(path).doc(user.id).get().then(userDB => {
        if (userDB.exists) {
          reject({ key: 'exists', message: 'User already exists' });
        } else {
          firestore.collection(path).doc(user.id).set(user.data).then(() => {
            resolve();
          }).catch(error => {
            console.error(error);
            reject( { key: 'defaulterror', message: 'Error adding user' });
          });
        }
      });
    });
  }

  removeUser(rootFirebasePath: string, userId: string): Promise<null> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      rootFirebasePath = rootFirebasePath || '';
      const path = (rootFirebasePath + '/users').replace('//', '/');
      firestore.collection(path).doc(userId).delete().then(() => {
        resolve();
      }).catch(error => {
        console.error(error);
        reject( { key: 'defaulterror', message: 'Error removing user' });
      });
    });
  }

  getUser(rootFirebasePath: string, userId: string): Promise<UserObj> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/users').replace('//', '/');
      firestore.collection(path).doc(userId).get().then(userDB => {
        resolve({
          id: userDB.id,
          data: userDB.data()
        } as UserObj);
      }).catch(error => {
        reject(error);
      });
    });
  }

  getUsers(rootFirebasePath: string): Promise<UserObj[]> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      if (!rootFirebasePath) {
        rootFirebasePath = '';
      }
      const path = (rootFirebasePath + '/users').replace('//', '/');
      firestore.collection(path).get().then(usersDB => {
        if (usersDB.empty) {
          resolve(null);
        } else {
          resolve(usersDB.docs.map(userDB => {
            return {
              id: userDB.id,
              data: userDB.data()
            } as UserObj;
          }));
        }
      }).catch(error => {
        reject(error);
      });
    });
  }

  updateUserPermissions(rootFirebasePath: string, user: UserObj) {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      rootFirebasePath = rootFirebasePath || '';
      const path = (rootFirebasePath + '/users').replace('//', '/');
      firestore.collection(path).doc(user.id).get().then(userDB => {
        if (!userDB.exists) {
          reject({ key: 'notexists', message: 'User not exists' });
        } else {
          firestore.collection(path).doc(user.id).update(
            { permissions: user.data.permissions }
          ).then(() => {
            resolve();
          }).catch(error => {
            console.error(error);
            reject( { key: 'defaulterror', message: 'Error updating permissions' });
          });
        }
      });
    });
  }
  //#endregion

  //#region Resources
  addResource(rootFirebasePath: string, resource: ResourceObj): Promise<string> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/resources').replace('//', '/');
      firestore.collection(path).add(resource.data).then(resourceCreated => {
        resolve(resourceCreated.id);
      }).catch(error => {
        reject(error);
      });
    });
  }

  removeResource(rootFirebasePath: string, resourceId: string): Promise<null> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const resourcePath = (rootFirebasePath + '/resources').replace('//', '/');
      const usersPath = (rootFirebasePath + '/users').replace('//', '/');
      const rolesPath = (rootFirebasePath + '/roles').replace('//', '/');
      const resourceRef = firestore.collection(resourcePath).doc(resourceId);
      Promise.all([
        this.getUsersByResource(rootFirebasePath, resourceId),
        this.getRolesByResource(rootFirebasePath, resourceId)
      ]).then(values => {
        const batch = firestore.batch();
        const usersToUpdate: UserObj[] = values[0];
        const rolesToUpdate: RoleObj[] = values[1];
        usersToUpdate.forEach(userToUpdate => {
          userToUpdate.data.permissions.splice(
            userToUpdate.data.permissions.findIndex(permission => permission.resource === resourceId), 1
          );
          const userRef = firestore.collection(usersPath).doc(userToUpdate.id);
          batch.update(userRef, { data: userToUpdate.data });
        });
        rolesToUpdate.forEach(roleToUpdate => {
          roleToUpdate.data.permissions.splice(
            roleToUpdate.data.permissions.findIndex(permission => permission.resource === resourceId), 1
          );
          const roleRef = firestore.collection(rolesPath).doc(roleToUpdate.id);
          batch.update(roleRef, { data: roleToUpdate.data });
        });
        batch.delete(resourceRef);
        batch.commit().then(() => {
          resolve();
        }).catch(error => {
          reject(error);
        });
      }).catch(error => {
        reject(error);
      });
    });
  }

  updateResource(rootFirebasePath: string, resource: ResourceObj): Promise<null> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/resources').replace('//', '/');
      firestore.collection(path).doc(resource.id).set(resource.data).then(() => {
        resolve();
      }).catch(error => {
        reject(error);
      });
    });
  }

  getResource(rootFirebasePath: string, resourceId: string): Promise<ResourceObj> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/resources').replace('//', '/');
      firestore.collection(path).doc(resourceId).get().then(resourceDB => {
        resolve({
          id: resourceDB.id,
          data: resourceDB.data()
        } as ResourceObj);
      }).catch(error => {
        reject(error);
      });
    });
  }

  getResources(rootFirebasePath: string): Promise<ResourceObj[]> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/resources').replace('//', '/');
      firestore.collection(path).get().then(resourcesDB => {
        if (resourcesDB.empty) {
          resolve(null);
        } else {
          resolve(resourcesDB.docs.map(resourceDB => {
            return {
              id: resourceDB.id,
              data: resourceDB.data()
            } as ResourceObj;
          }));
        }
      }).catch(error => {
        reject(error);
      });
    });
  }
  // endregion

  //#region Roles
  addRole(rootFirebasePath: string, role: RoleObj): Promise<string> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      rootFirebasePath = rootFirebasePath || '';
      const path = (rootFirebasePath + '/roles').replace('//', '/');
      firestore.collection(path).where('name', '==', role.data.name).get().then(roleDB => {
        if (!roleDB.empty) {
          reject({ key: 'exists', message: 'Role already exists' });
        } else {
          firestore.collection(path).add(role.data).then(() => {
            resolve();
          }).catch(error => {
            console.error(error);
            reject( { key: 'defaulterror', message: 'Error adding role' });
          });
        }
      });
    });
  }

  removeRole(rootFirebasePath: string, roleId: string): Promise<null> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      rootFirebasePath = rootFirebasePath || '';
      const path = (rootFirebasePath + '/roles').replace('//', '/');
      firestore.collection(path).doc(roleId).delete().then(() => {
        resolve();
      }).catch(error => {
        console.error(error);
        reject( { key: 'defaulterror', message: 'Error removing role' });
      });
    });
  }

  updateRole(rootFirebasePath: string, role: RoleObj): Promise<null> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      rootFirebasePath = rootFirebasePath || '';
      const path = (rootFirebasePath + '/roles').replace('//', '/');
      firestore.collection(path).doc(role.id).set(role.data).then(() => {
        resolve();
      }).catch(error => {
        console.error(error);
        reject( { key: 'defaulterror', message: 'Error updating role' });
      });
    });
  }

  getRole(rootFirebasePath: string, roleId: string): Promise<RoleObj> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/roles').replace('//', '/');
      firestore.collection(path).doc(roleId).get().then(roleDB => {
        resolve({
          id: roleDB.id,
          data: roleDB.data()
        } as RoleObj);
      }).catch(error => {
        reject(error);
      });
    });
  }

  getRoles(rootFirebasePath: string): Promise<RoleObj[]> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      if (!rootFirebasePath) {
        rootFirebasePath = '';
      }
      const path = (rootFirebasePath + '/roles').replace('//', '/');
      firestore.collection(path).get().then(rolesDB => {
        if (rolesDB.empty) {
          resolve(null);
        } else {
          resolve(rolesDB.docs.map(roleDB => {
            return {
              id: roleDB.id,
              data: roleDB.data()
            } as RoleObj;
          }));
        }
      }).catch(error => {
        reject(error);
      });
    });
  }

  getRolesByResource(rootFirebasePath: string, resourceId: string): Promise<RoleObj[]> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/roles').replace('//', '/');
      firestore.collection(path).where('data.resources', 'array-contains', resourceId).get().then(rolesDB => {
        if (rolesDB.empty) {
          resolve(null);
        } else {
          resolve(rolesDB.docs.map(roleDB => {
            return {
              id: roleDB.id,
              data: roleDB.data()
            } as RoleObj;
          }));
        }
      }).catch(error => {
        reject(error);
      });
    });
  }

  getRolesByUser(rootFirebasePath: string, userId: string): Promise<RoleObj[]> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/roles').replace('//', '/');
      firestore.collection(path).where('users', 'array-contains', userId).get().then(rolesDB => {
        if (rolesDB.empty) {
          resolve(null);
        } else {
          resolve(rolesDB.docs.map(roleDB => {
            return {
              id: roleDB.id,
              data: roleDB.data()
            } as RoleObj;
          }));
        }
      }).catch(error => {
        reject(error);
      });
    });
  }

  updateRolePermissions(rootFirebasePath: string, role: RoleObj) {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      rootFirebasePath = rootFirebasePath || '';
      const path = (rootFirebasePath + '/roles').replace('//', '/');
      firestore.collection(path).doc(role.id).get().then(roleDB => {
        if (!roleDB.exists) {
          reject({ key: 'notexists', message: 'Role not exists' });
        } else {
          firestore.collection(path).doc(role.id).update(
            { permissions: role.data.permissions }
          ).then(() => {
            resolve();
          }).catch(error => {
            console.error(error);
            reject( { key: 'defaulterror', message: 'Error updating permissions' });
          });
        }
      });
    });
  }
  // endregion

  //#region Roles
  removeUserFromRole(rootFirebasePath: string, userId: string): Promise<null> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const usersPath = (rootFirebasePath + '/users').replace('//', '/');
      const rolesPath = (rootFirebasePath + '/roles').replace('//', '/');
      this.getRolesByUser(rootFirebasePath, userId).then(rolesDB => {
        const batch = firestore.batch();
        const rolesToUpdate: RoleObj[] = rolesDB;
        rolesToUpdate.forEach(roleToUpdate => {
          roleToUpdate.data.users.splice(roleToUpdate.data.users.findIndex(user => user === userId), 1);
          const roleRef = firestore.collection(rolesPath).doc(roleToUpdate.id);
          batch.update(roleRef, { data: roleToUpdate.data });
        });
        const userRef = firestore.collection(usersPath).doc(userId);
        batch.delete(userRef);
        batch.commit().then(() => {
          resolve();
        }).catch(error => {
          reject(error);
        });
      }).catch(error => {
        reject(error);
      });
    });
  }



  getUsersByResource(rootFirebasePath: string, resourceId: string): Promise<UserObj[]> {
    return new Promise((resolve, reject) => {
      const firestore = firebase.firestore();
      const path = (rootFirebasePath + '/users').replace('//', '/');
      firestore.collection(path).where('data.resources', 'array-contains', resourceId).get().then(usersDB => {
        if (usersDB.empty) {
          resolve(null);
        } else {
          resolve(usersDB.docs.map(userDB => {
            return {
              id: userDB.id,
              data: userDB.data()
            } as UserObj;
          }));
        }
      }).catch(error => {
        reject(error);
      });
    });
  }
  // endregion
}
